import React, { useState, useEffect, useRef } from 'react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

// Score toujours en niveaux de gris (règle du design system)
const scoreShade = (s) => { const v = Number(s) || 0; return v >= 80 ? '#14161d' : v >= 65 ? '#3a4252' : v >= 50 ? '#5a6270' : '#878e9c'; };
const verdictOf = (s) => s >= 80 ? 'Forte adéquation' : s >= 65 ? 'Bonne adéquation' : s >= 50 ? 'Adéquation partielle' : 'Adéquation à challenger';
// Placeholder déterministe (à remplacer par le vrai moteur) — spec §3
const matchScoreFor = (candidate, besoin) => {
  const s = `${candidate?.id}|${besoin?.id}`;
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const jitter = (h % 17) - 8;
  const base = Number(candidate?.score) || 60;
  return Math.max(35, Math.min(97, Math.round(base) + jitter));
};
const initials = (t) => (t || '··').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

/**
 * Association Candidat ↔ Besoin — modale 3 étapes (pick → matching animé → done).
 * mode 'pickBesoin' : anchor = candidat, on choisit un besoin.
 * mode 'pickCandidate' : anchor = besoin, on choisit un candidat.
 */
export default function AssignMatchingModal({ mode, anchor, onClose, onDone, showNotification }) {
  const pickBesoin = mode === 'pickBesoin';
  const [step, setStep] = useState('pick');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [progress, setProgress] = useState(0);
  const [finalScore, setFinalScore] = useState(0);
  const animRef = useRef(null);
  const authH = { Authorization: `Bearer ${APIService.getToken()}` };

  const anchorName = pickBesoin ? `${anchor.prenom || ''} ${anchor.nom || anchor.name || ''}`.trim() : (anchor.titre || anchor.title || 'Besoin');

  useEffect(() => {
    (async () => {
      try {
        const url = pickBesoin ? `${BASE}/besoins?limit=200` : `${BASE}/candidats/vue?limit=500`;
        const r = await fetch(url, { headers: authH });
        const j = await r.json();
        setOptions(j.data || j.besoins || (Array.isArray(j) ? j : []));
      } catch (e) { showNotification?.('Erreur chargement : ' + e.message, 'error'); }
      finally { setLoading(false); }
    })();
    return () => { if (animRef.current) clearInterval(animRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const linkedNames = new Set((pickBesoin ? (anchor.positionnements || []).map(p => p.titre || p.besoin_titre) : (anchor.candidates || anchor.candidats || []).map(c => c.name || c.nom)).filter(Boolean));
  const q = query.trim().toLowerCase();
  const filtered = options.filter(o => {
    const label = pickBesoin ? `${o.titre || ''} ${o.client || ''}` : `${o.prenom || ''} ${o.nom || o.name || ''} ${o.titre || ''}`;
    if (linkedNames.has(pickBesoin ? o.titre : (o.nom || o.name))) return false;
    return !q || label.toLowerCase().includes(q);
  });

  // Enregistre le lien candidat↔besoin. Renvoie { ok } selon la vraie réponse HTTP (pas de succès optimiste).
  const commit = async (tgt) => {
    try {
      const url = pickBesoin ? `${BASE}/candidates/${anchor.id}/besoins` : `${BASE}/besoins/${anchor.id}/candidats`;
      const body = pickBesoin ? { besoin_id: tgt.id } : { candidateId: tgt.id };
      const r = await fetch(url, { method: 'POST', headers: { ...authH, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || `HTTP ${r.status}`); }
      return { ok: true };
    } catch (e) { return { ok: false, error: e.message }; }
  };

  // Récupère le vrai score de matching (repli sur le placeholder déterministe si l'endpoint n'est pas actif)
  const fetchScore = async (cand, bes) => {
    try {
      const qs = new URLSearchParams({ candidate_id: String(cand?.id ?? ''), besoin_id: String(bes?.id ?? '') });
      const r = await fetch(`${BASE}/matching/score?${qs.toString()}`, { headers: authH });
      if (r.ok) { const j = await r.json(); const s = Number(j.score ?? j.data?.score); if (Number.isFinite(s)) return Math.max(0, Math.min(100, Math.round(s))); }
    } catch (e) {}
    return matchScoreFor(cand, bes);
  };

  const onPick = async (tgt) => {
    const cand = pickBesoin ? anchor : tgt;
    const bes = pickBesoin ? tgt : anchor;
    setTarget(tgt); setProgress(0); setStep('matching');
    const score = await fetchScore(cand, bes);
    setFinalScore(score);
    const t0 = Date.now(); const dur = 950;
    animRef.current = setInterval(() => {
      const t = Math.min(1, (Date.now() - t0) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(Math.round(score * eased));
      if (t >= 1) {
        clearInterval(animRef.current); animRef.current = null; setProgress(score);
        commit(tgt).then(res => {
          if (res.ok) setStep('done');
          else { showNotification?.('Positionnement non enregistré : ' + res.error, 'error'); onClose(); }
        });
      }
    }, 25);
  };

  const targetTitle = target ? (pickBesoin ? target.titre : `${target.prenom || ''} ${target.nom || target.name || ''}`.trim()) : '';
  const sc = scoreShade(finalScore);

  return (
    <div onClick={step === 'matching' ? undefined : onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(4,6,11,0.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: '16px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <style>{`@keyframes am-pop{0%{transform:scale(.3)}60%{transform:scale(1.16)}100%{transform:scale(1)}}@keyframes am-spin{to{transform:rotate(360deg)}}`}</style>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel, #fff)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '480px', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '16px 20px', borderBottom: '1px solid var(--hair)', flexShrink: 0 }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'var(--accent-soft-bg)', color: 'var(--text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="people" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>{pickBesoin ? 'Ajouter à un besoin' : 'Ajouter un candidat'}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{anchorName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: '6px', borderRadius: '7px', fontSize: '14px' }}><BI icon="x-lg" /></button>
        </div>

        {/* PICK */}
        {step === 'pick' && (<>
          <div style={{ padding: '14px 20px 0', flexShrink: 0 }}>
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder={pickBesoin ? 'Rechercher un besoin, un client…' : 'Rechercher un candidat…'}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '13.5px', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px 14px', maxHeight: '360px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-faint)', fontSize: '13px' }}><BI icon="arrow-repeat" style={{ animation: 'am-spin .7s linear infinite' }} /> Chargement…</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-faint)', fontSize: '13px' }}>{q ? 'Aucun résultat.' : (pickBesoin ? 'Tous les besoins compatibles sont déjà liés à ce candidat.' : 'Tous les candidats compatibles sont déjà rattachés.')}</div>
            ) : filtered.map(o => {
              const title = pickBesoin ? o.titre : `${o.prenom || ''} ${o.nom || o.name || ''}`.trim();
              const sub = pickBesoin ? [o.client, o.ville].filter(Boolean).join(' · ') : (o.titre || '');
              const tag = pickBesoin ? (o.priorite || o.statut) : (o.statut || '');
              return (
                <button key={o.id} onClick={() => onPick(o)} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 12px', marginBottom: '6px', borderRadius: '9px', border: '1px solid var(--hair)', background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--ghost-bg)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ width: '34px', height: '34px', borderRadius: '9px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace", background: 'var(--chip-bg)', color: 'var(--text-strong)' }}>{initials(title)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title || '—'}</span>
                    {sub && <span style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>{sub}</span>}
                  </span>
                  {tag && <span style={{ flex: 'none', fontSize: '11px', color: 'var(--text-3)' }}>{tag}</span>}
                </button>
              );
            })}
          </div>
        </>)}

        {/* MATCHING */}
        {step === 'matching' && (
          <div style={{ padding: '30px 24px 34px', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '18px' }}>Analyse du matching…</div>
            <div style={{ width: '136px', height: '136px', borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `conic-gradient(${sc} ${progress * 3.6}deg, var(--code-bg) 0deg)` }}>
              <div style={{ width: '106px', height: '106px', borderRadius: '50%', background: 'var(--panel, #fff)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '28px', fontWeight: 700, color: sc, lineHeight: 1 }}>{progress}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>/ 100</span>
              </div>
            </div>
            <div style={{ marginTop: '16px', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{pickBesoin ? anchorName : targetTitle} ↔ {pickBesoin ? targetTitle : anchorName}</div>
            <div style={{ marginTop: '3px', fontSize: '11.5px', color: 'var(--text-3)' }}>Compétences · séniorité · disponibilité · localisation</div>
          </div>
        )}

        {/* DONE */}
        {step === 'done' && (
          <div style={{ padding: '28px 24px 22px', textAlign: 'center' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '14px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(45,200,110,0.16)', color: '#16a34a', fontSize: '26px', animation: 'am-pop .5s cubic-bezier(.34,1.56,.64,1)' }}><BI icon="check-lg" /></div>
            <div style={{ marginTop: '16px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '32px', fontWeight: 700, color: sc, lineHeight: 1 }}>{finalScore}<span style={{ fontSize: '14px', color: 'var(--text-3)' }}> /100</span></div>
            <div style={{ marginTop: '6px', fontSize: '13.5px', fontWeight: 700, color: 'var(--text-strong)' }}>{verdictOf(finalScore)}</div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-2)' }}>{pickBesoin ? anchorName : targetTitle} a été rattaché à <b style={{ color: 'var(--text-strong)' }}>{pickBesoin ? targetTitle : anchorName}</b>.</div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px' }}>
              <button onClick={() => { onDone?.(target, finalScore); showNotification?.(`${pickBesoin ? anchorName : targetTitle} → ${pickBesoin ? targetTitle : anchorName}`, 'success'); onClose(); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 18px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}><BI icon="check2" /> Terminé</button>
              <button onClick={() => { onDone?.(target, finalScore); if (target?.id != null) window.location.hash = (pickBesoin ? '/besoins/' : '/candidats/') + target.id; onClose(); }}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>{pickBesoin ? 'Voir le besoin' : 'Voir le candidat'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
